export interface AgentAccessInvite {
  id: string;
  tenantId: string;
  createdBy?: string | null;
  kind: 'link' | 'code' | string;
  code: string;
  status: 'active' | 'expired' | 'disabled' | 'consumed' | string;
  allowedEmail?: string | null;
  approvalRequired: boolean;
  defaultRoleCode: string;
  expiresAt?: string | null;
  maxUses?: number | null;
  useCount: number;
  metadata?: Record<string, unknown> | null;
  createdAt: string;
  updatedAt?: string | null;
}

export interface AgentAccessRequestRecord {
  id: string;
  tenantId: string;
  userId: string;
  inviteId?: string | null;
  status: 'requested' | 'approved' | 'rejected' | 'revoked' | string;
  roleCode: string;
  metadata?: Record<string, unknown> | null;
  requestedAt: string;
  reviewedAt?: string | null;
  reviewedBy?: string | null;
  tenantName?: string;
  tenantSlug?: string;
  userEmail?: string | null;
  allowedEmail?: string | null;
  inviteKind?: string | null;
  inviteStatus?: string | null;
  membershipId?: string | null;
}

export interface AgentAccessSummary {
  requests: AgentAccessRequestRecord[];
}

export interface AgentAccessRedeemResult {
  request: AgentAccessRequestRecord;
  membership?: {
    id: string;
    tenantId: string;
    userId: string;
    role: string;
  } | null;
  createdRequest: boolean;
  approved: boolean;
  alreadyApproved: boolean;
}

export interface AgentAccessInviteCreatePayload {
  kind: 'link' | 'code';
  allowedEmail?: string | null;
  approvalRequired?: boolean;
  defaultRoleCode?: string;
  expiresAt?: string | null;
  maxUses?: number | null;
  metadata?: Record<string, unknown> | null;
}

interface AgentAccessInviteListResponse {
  items?: AgentAccessInvite[];
}

interface AgentAccessRequestListResponse {
  items?: AgentAccessRequestRecord[];
}

export class AgentAccessError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'AgentAccessError';
    this.status = status;
  }
}

async function readJSON<T>(response: Response): Promise<T> {
  if (response.status === 204) {
    return undefined as T;
  }
  return response.json() as Promise<T>;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
    ...init,
  });

  if (!response.ok) {
    let message = `Request failed with status ${response.status}`;
    try {
      const body = await response.json() as { message?: string; detail?: string; title?: string };
      message = body.message || body.detail || body.title || message;
    } catch {
      // ignore body parsing failures
    }
    throw new AgentAccessError(message, response.status);
  }

  return readJSON<T>(response);
}

function buildStatusQuery(statuses?: string[]): string {
  if (!statuses?.length) {
    return '';
  }
  const query = new URLSearchParams();
  statuses.filter(Boolean).forEach(status => query.append('status', status));
  const encoded = query.toString();
  return encoded ? `?${encoded}` : '';
}

export async function getAgentAccessSummary(): Promise<AgentAccessSummary> {
  return request<AgentAccessSummary>('/api/v1/agent-access/me');
}

export async function redeemAgentAccessCode(code: string): Promise<AgentAccessRedeemResult> {
  return request<AgentAccessRedeemResult>('/api/v1/agent-access/redeem', {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
}

export async function listTenantAgentAccessInvites(tenantId: string): Promise<AgentAccessInvite[]> {
  const payload = await request<AgentAccessInviteListResponse>(`/api/v1/agent-access/tenants/${encodeURIComponent(tenantId)}/invites`);
  return payload.items ?? [];
}

export async function createTenantAgentAccessInvite(tenantId: string, payload: AgentAccessInviteCreatePayload): Promise<AgentAccessInvite> {
  return request<AgentAccessInvite>(`/api/v1/agent-access/tenants/${encodeURIComponent(tenantId)}/invites`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });
}

export async function listTenantAgentAccessRequests(tenantId: string, statuses?: string[]): Promise<AgentAccessRequestRecord[]> {
  const payload = await request<AgentAccessRequestListResponse>(
    `/api/v1/agent-access/tenants/${encodeURIComponent(tenantId)}/requests${buildStatusQuery(statuses)}`,
  );
  return payload.items ?? [];
}

async function updateTenantAgentAccessRequest(tenantId: string, requestId: string, action: 'approve' | 'reject' | 'revoke') {
  return request<AgentAccessRequestRecord>(
    `/api/v1/agent-access/tenants/${encodeURIComponent(tenantId)}/requests/${encodeURIComponent(requestId)}/${action}`,
    { method: 'POST' },
  );
}

export function approveTenantAgentAccessRequest(tenantId: string, requestId: string) {
  return updateTenantAgentAccessRequest(tenantId, requestId, 'approve');
}

export function rejectTenantAgentAccessRequest(tenantId: string, requestId: string) {
  return updateTenantAgentAccessRequest(tenantId, requestId, 'reject');
}

export function revokeTenantAgentAccessRequest(tenantId: string, requestId: string) {
  return updateTenantAgentAccessRequest(tenantId, requestId, 'revoke');
}
